import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useMemo, useState } from "react";
import { getConfigService } from "../../../services/configService";
import { Config } from "../../../models/Config";
import SettingHeader from "./SettingHeader";
import SettingSidebar from "./SettingSidebar";
import AlertMessage from "./AlertMessage";
import { TABS } from "../../../constants/modelOptions";
// Import Tab Components
import GeneralTab from "./tabs/GeneralTab";
import CarouselChatTab from "./tabs/CarouselChatTab";
import QuickFixTab from "./tabs/QuickFixTab";
import ExcelExportTab from "./tabs/ExcelExportTab";
import ExcelFormattingTab from "./tabs/ExcelFormattingTab";
import KeyboardShortcutsTab from "./tabs/KeyboardShortcutsTab";
import ModelWalkthroughTab from "./tabs/ModelWalkthroughTab";
import PromptLibraryTab from "./tabs/PromptLibraryTab";
import IntegrationsTab from "./tabs/IntegrationsTab";
import AdvancedTab from "./tabs/AdvancedTab";
import IndexingTab from "./tabs/IndexingTab";
const SettingsApp = ({ initialTab = 0, hiddenTabs = [], captureWorkbookSnapshotBase64, indexingContext, }) => {
    const visibleTabs = useMemo(() => TABS.filter((tab) => !hiddenTabs.includes(tab.id)), [hiddenTabs]);
    const [configService] = useState(() => getConfigService());
    const [config, setConfig] = useState(() => configService.getConfig());
    const [selectedTab, setSelectedTab] = useState(visibleTabs[initialTab]?.id || visibleTabs[0]?.id || "general");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [currentDeckOverrideEnabled, setCurrentDeckOverrideEnabled] = useState(indexingContext?.currentDeckOverrideEnabled ?? null);
    React.useEffect(() => {
        let mounted = true;
        const hydrateExcelColors = async () => {
            try {
                await configService.hydrateExcelColorsFromBackend();
                if (!mounted) {
                    return;
                }
                setConfig(Object.assign(new Config(), configService.getConfig()));
            }
            catch (error) {
                console.warn("[SettingsApp] Excel color hydration skipped:", error);
            }
        };
        void hydrateExcelColors();
        return () => {
            mounted = false;
        };
    }, [configService]);
    React.useEffect(() => {
        if (!visibleTabs.some((tab) => tab.id === selectedTab)) {
            setSelectedTab(visibleTabs[0]?.id || "general");
        }
    }, [selectedTab, visibleTabs]);
    const updateConfig = (key, value) => {
        try {
            // Get the config from service (to ensure we have the latest reference)
            const serviceConfig = configService.getConfig();
            // Update the service's config object directly
            serviceConfig[key] = value;
            // Save to localStorage immediately
            configService.saveConfig();
            // Update React state with a new copy for re-render
            const newConfig = Object.assign(new Config(), serviceConfig);
            setConfig(newConfig);
            console.log(`Config saved: ${String(key)} = ${value}`);
            // Notify parent window (taskpane) of settings change if in Office dialog
            if (window.Office && Office.context.ui) {
                try {
                    Office.context.ui.messageParent(JSON.stringify({
                        type: 'settingsUpdated',
                        key: String(key),
                        value: value,
                        timestamp: Date.now(),
                    }));
                }
                catch (err) {
                    // Not in dialog context, that's okay
                    console.log('[SettingsApp] Not in dialog context, skipping message to parent');
                }
            }
            // Clear any previous errors
            setShowError(false);
        }
        catch (error) {
            console.error("Error saving config:", error);
            setErrorMessage("Failed to save settings");
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        }
    };
    const handleTabChange = (tabId) => {
        setSelectedTab(tabId);
    };
    const handleCurrentDeckOverrideChange = (enabled) => {
        const documentId = indexingContext?.currentdocumentId;
        if (!documentId) {
            return;
        }
        setCurrentDeckOverrideEnabled(enabled);
        if (window.Office && Office.context.ui) {
            try {
                Office.context.ui.messageParent(JSON.stringify({
                    type: "deckIndexingUpdated",
                    documentId,
                    enabled,
                    timestamp: Date.now(),
                }));
            }
            catch (error) {
                console.warn("[SettingsApp] Could not notify parent about deck indexing update:", error);
            }
        }
    };
    return (_jsxs("div", { className: "flex flex-col h-screen w-full bg-gray-100 font-sans", children: [_jsx(SettingHeader, {}), _jsxs("div", { className: "flex flex-1 overflow-hidden", children: [_jsx(SettingSidebar, { tabs: visibleTabs, activeTab: selectedTab, onTabChange: handleTabChange }), _jsx("div", { className: "flex-1 overflow-y-auto p-3 px-6 pb-6 bg-white", children: _jsxs("div", { className: "max-w-[700px]", children: [showError && _jsx(AlertMessage, { type: "error", message: errorMessage }), selectedTab === "general" && _jsx(GeneralTab, { config: config, updateConfig: updateConfig }), selectedTab === "carousel-chat" && _jsx(CarouselChatTab, { config: config, updateConfig: updateConfig }), selectedTab === "quick-fix" && _jsx(QuickFixTab, { config: config, updateConfig: updateConfig }), selectedTab === "excel-export" && _jsx(ExcelExportTab, { config: config, updateConfig: updateConfig }), selectedTab === "excel-formatting" && (_jsx(ExcelFormattingTab, { config: config, updateConfig: updateConfig, captureWorkbookSnapshotBase64: captureWorkbookSnapshotBase64 })), selectedTab === "keyboard-shortcuts" && _jsx(KeyboardShortcutsTab, { config: config, updateConfig: updateConfig }), selectedTab === "model-walkthrough" && _jsx(ModelWalkthroughTab, { config: config, updateConfig: updateConfig }), selectedTab === "prompt-library" && _jsx(PromptLibraryTab, { config: config, updateConfig: updateConfig }), selectedTab === "integrations" && _jsx(IntegrationsTab, { config: config, updateConfig: updateConfig }), selectedTab === "advanced" && _jsx(AdvancedTab, { config: config, updateConfig: updateConfig }), selectedTab === "indexing" && (_jsx(IndexingTab, { config: config, updateConfig: updateConfig, currentdocumentId: indexingContext?.currentdocumentId, currentDeckName: indexingContext?.currentDeckName, currentDeckOverrideEnabled: currentDeckOverrideEnabled, onUpdateCurrentDeckOverride: handleCurrentDeckOverrideChange }))] }) })] })] }));
};
export default SettingsApp;
